import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { StatusBadge, EmptyState } from "@/components/common";
import { fmtDate } from "@/lib/helpers";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import * as Icons from "lucide-react";

function ImportClientContacts({ source, list, onChange, testPrefix }) {
  const avail = (source || []).filter((c) => c.name || c.phone || c.email);
  if (avail.length === 0) return null;
  const same = (a, b) => (a.name || "") === (b.name || "") && (a.phone || "") === (b.phone || "") && (a.email || "") === (b.email || "");
  const pick = (v) => {
    const chosen = v === "all" ? avail : [avail[Number(v)]];
    const fresh = chosen.filter((c) => !list.some((l) => same(l, c))).map((c) => ({ name: c.name || "", role: c.role || "", phone: c.phone || "", email: c.email || "" }));
    if (fresh.length === 0) { toast.info("Ce contact est déjà sur le site"); return; }
    onChange([...list.filter((l) => l.name || l.phone || l.email), ...fresh]);
    toast.success(fresh.length > 1 ? `${fresh.length} contacts repris de la fiche client` : "Contact repris de la fiche client");
  };
  return (
    <select value="" onChange={(e) => e.target.value && pick(e.target.value)} className="h-9 w-full rounded-md border border-emerald-200 bg-emerald-50 px-2 text-sm text-emerald-800" data-testid={`${testPrefix}-import-contact`}>
      <option value="">+ Reprendre un contact de la fiche client…</option>
      {avail.map((c, i) => <option key={i} value={i}>{[c.name, c.role, c.phone, c.email].filter(Boolean).join(" · ")}</option>)}
      {avail.length > 1 && <option value="all">Tous les contacts du client</option>}
    </select>
  );
}

function ContactsEditor({ contacts, onChange, testPrefix, clientContacts }) {
  const list = contacts || [];
  const add = () => onChange([...list, { name: "", role: "", phone: "", email: "" }]);
  const remove = (i) => onChange(list.filter((_, idx) => idx !== i));
  const set = (i, k, v) => onChange(list.map((c, idx) => (idx === i ? { ...c, [k]: v } : c)));
  return (
    <div className="space-y-2.5">
      <div className="flex items-center justify-between">
        <Label>Contacts</Label>
        <Button type="button" variant="outline" size="sm" className="h-7" onClick={add} data-testid={`${testPrefix}-add-contact`}>
          <Icons.Plus className="mr-1 h-3.5 w-3.5" />Ajouter un contact</Button>
      </div>
      <ImportClientContacts source={clientContacts} list={list} onChange={onChange} testPrefix={testPrefix} />
      {list.length === 0 && <p className="text-xs text-slate-400">Aucun contact. Ajoutez-en un ou plusieurs.</p>}
      {list.map((c, i) => (
        <div key={i} className="space-y-2 rounded-lg border border-slate-200 p-3" data-testid={`${testPrefix}-contact-${i}`}>
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-slate-500">{i === 0 ? "Contact principal" : `Contact ${i + 1}`}</span>
            <Button type="button" variant="ghost" size="sm" className="h-7 text-rose-600" onClick={() => remove(i)} data-testid={`${testPrefix}-remove-contact-${i}`}><Icons.Trash2 className="h-3.5 w-3.5" /></Button>
          </div>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <Input placeholder="Nom" value={c.name || ""} onChange={(e) => set(i, "name", e.target.value)} data-testid={`${testPrefix}-contact-${i}-name`} />
            <Input placeholder="Fonction" value={c.role || ""} onChange={(e) => set(i, "role", e.target.value)} data-testid={`${testPrefix}-contact-${i}-role`} />
            <Input placeholder="Téléphone" value={c.phone || ""} onChange={(e) => set(i, "phone", e.target.value)} data-testid={`${testPrefix}-contact-${i}-phone`} />
            <Input placeholder="E-mail" value={c.email || ""} onChange={(e) => set(i, "email", e.target.value)} data-testid={`${testPrefix}-contact-${i}-email`} />
          </div>
        </div>
      ))}
    </div>
  );
}

function ClientForm({ initial, onSave, testPrefix }) {
  const [f, setF] = useState(initial || { name: "", notes: "", siret: "", siren: "", address: "", code_postal: "", commune: "", contacts: [] });
  const [siret, setSiret] = useState(initial?.siret || "");
  const [searching, setSearching] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const lookup = async () => {
    const code = (siret || "").replace(/\D/g, "");
    if (code.length !== 9 && code.length !== 14) { toast.error("Saisissez un SIREN (9 chiffres) ou un SIRET (14 chiffres)"); return; }
    setSearching(true);
    try {
      const { data } = await api.get("/clients/pappers", { params: { siret: code } });
      setF((prev) => ({
        ...prev,
        name: data.name || prev.name,
        siret: data.siret || code,
        siren: data.siren || prev.siren,
        address: prev.address || data.address || "",
        code_postal: prev.code_postal || data.code_postal || "",
        commune: prev.commune || data.commune || "",
      }));
      setSiret(data.siret || code);
      toast.success("Entreprise trouvée");
    } catch (e) { toast.error(apiError(e)); }
    finally { setSearching(false); }
  };

  return (
    <div className="space-y-3">
      <div className="space-y-1.5"><Label>Recherche entreprise (SIRET / SIREN · Pappers)</Label>
        <div className="flex gap-2">
          <Input value={siret} onChange={(e) => setSiret(e.target.value)} placeholder="N° SIRET (14) ou SIREN (9)" inputMode="numeric" data-testid={`${testPrefix}-siret`} />
          <Button type="button" variant="outline" onClick={lookup} disabled={searching} data-testid={`${testPrefix}-siret-search`}>
            {searching ? "…" : <><Icons.Search className="mr-1 h-4 w-4" />Rechercher</>}</Button>
        </div>
      </div>
      <div className="space-y-1.5"><Label>Nom ou raison sociale *</Label>
        <Input value={f.name} onChange={set("name")} data-testid={`${testPrefix}-name`} /></div>
      <div className="space-y-1.5"><Label>Adresse</Label>
        <Input value={f.address || ""} onChange={set("address")} placeholder="N° et rue" data-testid={`${testPrefix}-address`} /></div>
      <div className="grid grid-cols-3 gap-2">
        <div className="space-y-1.5"><Label>Code postal</Label>
          <Input value={f.code_postal || ""} onChange={set("code_postal")} inputMode="numeric" data-testid={`${testPrefix}-code-postal`} /></div>
        <div className="col-span-2 space-y-1.5"><Label>Ville</Label>
          <Input value={f.commune || ""} onChange={set("commune")} data-testid={`${testPrefix}-commune`} /></div>
      </div>
      <ContactsEditor contacts={f.contacts} onChange={(contacts) => setF({ ...f, contacts })} testPrefix={testPrefix} />
      <div className="space-y-1.5"><Label>Notes</Label>
        <Textarea value={f.notes} onChange={set("notes")} data-testid={`${testPrefix}-notes`} /></div>
      <DialogFooter>
        <Button onClick={() => onSave(f)} disabled={!f.name.trim()} data-testid={`${testPrefix}-save`}
          className="bg-emerald-600 hover:bg-emerald-700">Enregistrer</Button>
      </DialogFooter>
    </div>
  );
}

function SiteForm({ initial, clientId, clientContacts, client, onSave, testPrefix }) {
  const [f, setF] = useState(initial || { name: "", address: "", commune: "", access_instructions: "", contacts: [] });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const hasClientAddr = client && (client.address || client.code_postal || client.commune);
  const copyAddr = () => setF({ ...f, address: client.address || "", commune: [client.code_postal, client.commune].filter(Boolean).join(" ") });
  return (
    <div className="space-y-3">
      <div className="space-y-1.5"><Label>Nom du site *</Label>
        <Input value={f.name} onChange={set("name")} data-testid={`${testPrefix}-name`} /></div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="space-y-1.5"><Label>Adresse</Label>
          <Input value={f.address} onChange={set("address")} data-testid={`${testPrefix}-address`} /></div>
        <div className="space-y-1.5"><Label>Commune</Label>
          <Input value={f.commune} onChange={set("commune")} data-testid={`${testPrefix}-commune`} /></div>
      </div>
      {hasClientAddr && (
        <Button type="button" variant="outline" size="sm" onClick={copyAddr} data-testid={`${testPrefix}-copy-client-address`}>
          <Icons.MapPin className="mr-1.5 h-4 w-4" />Reprendre l'adresse du client
        </Button>
      )}
      <ContactsEditor contacts={f.contacts} onChange={(contacts) => setF({ ...f, contacts })} testPrefix={testPrefix} clientContacts={clientContacts} />
      <div className="space-y-1.5"><Label>Instructions d'accès</Label>
        <Textarea value={f.access_instructions} onChange={set("access_instructions")} data-testid={`${testPrefix}-access`} /></div>
      <DialogFooter>
        <Button onClick={() => onSave({ ...f, client_id: clientId })} disabled={!f.name.trim()} data-testid={`${testPrefix}-save`}
          className="bg-emerald-600 hover:bg-emerald-700">Enregistrer</Button>
      </DialogFooter>
    </div>
  );
}

function ContactList({ contacts, testId }) {
  if (!contacts || contacts.length === 0) return null;
  return (
    <div className="mt-2 space-y-1.5" data-testid={testId}>
      {contacts.map((c, i) => (
        <div key={i} className="flex flex-wrap items-center gap-x-4 gap-y-0.5 text-sm text-slate-600">
          <span className="flex items-center gap-1.5 font-medium text-slate-700"><Icons.User className="h-3.5 w-3.5 text-slate-400" />{c.name || "—"}{c.role ? ` · ${c.role}` : ""}</span>
          {c.phone && <span className="flex items-center gap-1.5"><Icons.Phone className="h-3.5 w-3.5 text-slate-400" />{c.phone}</span>}
          {c.email && <span className="flex items-center gap-1.5"><Icons.Mail className="h-3.5 w-3.5 text-slate-400" />{c.email}</span>}
        </div>
      ))}
    </div>
  );
}

export default function Clients() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasPerm } = useAuth();
  const canManage = hasPerm("clients.manage");
  const [clients, setClients] = useState([]);
  const [search, setSearch] = useState("");
  const [detail, setDetail] = useState(null);
  const [openNew, setOpenNew] = useState(false);
  const [openSite, setOpenSite] = useState(false);
  const [editClient, setEditClient] = useState(false);
  const [editSite, setEditSite] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get("/clients", { params: { search } });
    setClients(data);
  }, [search]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (id) api.get(`/clients/${id}`).then((r) => setDetail(r.data)).catch(() => navigate("/clients"));
    else setDetail(null);
  }, [id, navigate]);

  const reloadDetail = () => id && api.get(`/clients/${id}`).then((r) => setDetail(r.data));

  const createClient = async (f) => {
    try { const { data } = await api.post("/clients", f); setOpenNew(false); toast.success("Client créé"); load(); navigate(`/clients/${data.id}`); }
    catch (e) { toast.error(apiError(e)); }
  };
  const saveClient = async (f) => {
    try { await api.put(`/clients/${id}`, f); setEditClient(false); toast.success("Client mis à jour"); reloadDetail(); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const createSite = async (f) => {
    try { await api.post("/sites", f); setOpenSite(false); toast.success("Site ajouté"); reloadDetail(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const saveSite = async (f) => {
    try { await api.put(`/sites/${editSite.id}`, f); setEditSite(null); toast.success("Site mis à jour"); reloadDetail(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const archiveSite = async (sid) => {
    try { await api.post(`/sites/${sid}/archive`); toast.success("Site archivé"); reloadDetail(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const deleteSite = async (sid) => {
    try { await api.delete(`/sites/${sid}`); toast.success("Site supprimé"); reloadDetail(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const archiveClient = async () => {
    try { await api.post(`/clients/${id}/archive`); toast.success("Client archivé"); navigate("/clients"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  if (detail) return (
    <div className="space-y-5" data-testid="client-detail-page">
      <button onClick={() => navigate("/clients")} className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700" data-testid="back-to-clients">
        <Icons.ChevronLeft className="h-4 w-4" /> Clients & sites
      </button>
      <Card className="p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="font-display text-2xl font-bold text-slate-900">{detail.name}</h1>
            {(detail.siret || detail.siren) && <p className="mt-0.5 font-mono-data text-xs text-slate-400">{detail.siret ? `SIRET ${detail.siret}` : `SIREN ${detail.siren}`}</p>}
            {(detail.address || detail.code_postal || detail.commune) && (
              <p className="mt-1 flex items-center gap-1.5 text-sm text-slate-500" data-testid="client-address">
                <Icons.MapPin className="h-3.5 w-3.5 text-slate-400" />
                {[detail.address, [detail.code_postal, detail.commune].filter(Boolean).join(" ")].filter(Boolean).join(", ")}
              </p>
            )}
            <ContactList contacts={detail.contacts} testId="client-contacts" />
            {detail.notes && <p className="mt-3 max-w-2xl rounded-lg bg-slate-50 p-3 text-sm text-slate-600">{detail.notes}</p>}
          </div>
          <div className="flex gap-2">
            <Dialog open={editClient} onOpenChange={setEditClient}>
              <DialogTrigger asChild><Button variant="outline" size="sm" data-testid="edit-client-btn"><Icons.Pencil className="mr-1.5 h-4 w-4" />Modifier</Button></DialogTrigger>
              <DialogContent className="max-h-[90vh] overflow-y-auto"><DialogHeader><DialogTitle>Modifier le client</DialogTitle></DialogHeader>
                <ClientForm initial={detail} onSave={saveClient} testPrefix="edit-client" /></DialogContent>
            </Dialog>
            {canManage && (
              <AlertDialog>
                <AlertDialogTrigger asChild><Button variant="outline" size="sm" className="text-rose-600" data-testid="archive-client-btn"><Icons.Archive className="mr-1.5 h-4 w-4" />Archiver</Button></AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader><AlertDialogTitle>Archiver ce client ?</AlertDialogTitle>
                    <AlertDialogDescription>Le client et ses sites seront archivés pour conserver l'historique des visites. Ils n'apparaîtront plus dans les listes actives.</AlertDialogDescription></AlertDialogHeader>
                  <AlertDialogFooter><AlertDialogCancel>Annuler</AlertDialogCancel>
                    <AlertDialogAction onClick={archiveClient} className="bg-rose-600 hover:bg-rose-700" data-testid="confirm-archive-client">Archiver</AlertDialogAction></AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </div>
        </div>
      </Card>

      <div className="flex items-center justify-between">
        <h2 className="font-display text-lg font-semibold text-slate-800">Sites ({detail.sites?.length || 0})</h2>
        <Dialog open={openSite} onOpenChange={setOpenSite}>
          <DialogTrigger asChild><Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" data-testid="add-site-btn"><Icons.Plus className="mr-1.5 h-4 w-4" />Ajouter un site</Button></DialogTrigger>
          <DialogContent className="max-h-[90vh] overflow-y-auto"><DialogHeader><DialogTitle>Nouveau site</DialogTitle></DialogHeader>
            <SiteForm clientId={detail.id} clientContacts={detail.contacts} client={detail} onSave={createSite} testPrefix="new-site" /></DialogContent>
        </Dialog>
      </div>

      {(detail.sites?.length || 0) === 0 ? (
        <EmptyState icon="Building" title="Aucun site" testId="sites-empty">Ajoutez le premier site de ce client.</EmptyState>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          {detail.sites.map((s) => (
            <Card key={s.id} className="p-4" data-testid={`site-card-${s.id}`}>
              <div className="flex items-start gap-3">
                <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-emerald-50 text-emerald-700"><Icons.Building2 className="h-5 w-5" /></div>
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-slate-800">{s.name}</p>
                  <p className="text-sm text-slate-500">{[s.address, s.commune].filter(Boolean).join(", ") || "Adresse non renseignée"}</p>
                  <ContactList contacts={s.contacts} testId={`site-contacts-${s.id}`} />
                  <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1">
                    <Button variant="link" size="sm" className="h-auto p-0 text-emerald-700"
                      onClick={() => navigate(`/clients/${detail.id}/site/${s.id}`)} data-testid={`site-open-${s.id}`}>
                      Ouvrir la fiche <Icons.ArrowRight className="ml-1 h-3.5 w-3.5" />
                    </Button>
                    <Button variant="link" size="sm" className="h-auto p-0 text-slate-500"
                      onClick={() => navigate(`/check?site=${s.id}&client=${detail.id}&new=1`)} data-testid={`site-new-visit-${s.id}`}>
                      Nouvelle visite ici
                    </Button>
                  </div>
                </div>
              </div>
              {canManage && (
                <div className="mt-3 flex justify-end gap-1 border-t border-slate-100 pt-2">
                  <Button variant="ghost" size="sm" className="text-slate-600" onClick={() => setEditSite(s)} data-testid={`site-edit-${s.id}`}><Icons.Pencil className="mr-1.5 h-3.5 w-3.5" />Modifier</Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild><Button variant="ghost" size="sm" className="text-slate-500" data-testid={`site-archive-${s.id}`}><Icons.Archive className="mr-1.5 h-3.5 w-3.5" />Archiver</Button></AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader><AlertDialogTitle>Archiver ce site ?</AlertDialogTitle>
                        <AlertDialogDescription>« {s.name} » sera masqué des listes actives, mais l'historique des visites est conservé.</AlertDialogDescription></AlertDialogHeader>
                      <AlertDialogFooter><AlertDialogCancel>Annuler</AlertDialogCancel>
                        <AlertDialogAction onClick={() => archiveSite(s.id)} data-testid={`confirm-archive-site-${s.id}`}>Archiver</AlertDialogAction></AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                  <AlertDialog>
                    <AlertDialogTrigger asChild><Button variant="ghost" size="sm" className="text-rose-600" data-testid={`site-delete-${s.id}`}><Icons.Trash2 className="mr-1.5 h-3.5 w-3.5" />Supprimer</Button></AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader><AlertDialogTitle>Supprimer définitivement ce site ?</AlertDialogTitle>
                        <AlertDialogDescription>« {s.name} » sera supprimé. Impossible s'il possède des visites — archivez-le dans ce cas. Cette action est irréversible.</AlertDialogDescription></AlertDialogHeader>
                      <AlertDialogFooter><AlertDialogCancel>Annuler</AlertDialogCancel>
                        <AlertDialogAction onClick={() => deleteSite(s.id)} className="bg-rose-600 hover:bg-rose-700" data-testid={`confirm-delete-site-${s.id}`}>Supprimer</AlertDialogAction></AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              )}
            </Card>
          ))}
        </div>
      )}

      <Dialog open={!!editSite} onOpenChange={(o) => !o && setEditSite(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto"><DialogHeader><DialogTitle>Modifier le site</DialogTitle></DialogHeader>
          {editSite && <SiteForm initial={editSite} clientId={detail.id} clientContacts={detail.contacts} onSave={saveSite} testPrefix="edit-site" />}</DialogContent>
      </Dialog>
    </div>
  );

  return (
    <div className="space-y-5" data-testid="clients-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold text-slate-900 lg:text-3xl">Clients & sites</h1>
          <p className="text-sm text-slate-500">Gérez vos clients et leurs sites d'intervention.</p>
        </div>
        <Dialog open={openNew} onOpenChange={setOpenNew}>
          <DialogTrigger asChild><Button className="bg-emerald-600 hover:bg-emerald-700" data-testid="new-client-btn"><Icons.Plus className="mr-1.5 h-4 w-4" />Nouveau client</Button></DialogTrigger>
          <DialogContent className="max-h-[90vh] overflow-y-auto"><DialogHeader><DialogTitle>Nouveau client</DialogTitle></DialogHeader>
            <ClientForm onSave={createClient} testPrefix="new-client" /></DialogContent>
        </Dialog>
      </div>

      <div className="relative max-w-md">
        <Icons.Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher un client…"
          className="pl-9" data-testid="client-search" />
      </div>

      {clients.length === 0 ? (
        <EmptyState icon="Building2" title="Aucun client" testId="clients-empty">Créez votre premier client pour démarrer.</EmptyState>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {clients.map((c) => (
            <Card key={c.id} className="cursor-pointer p-4 transition-shadow hover:shadow-md" onClick={() => navigate(`/clients/${c.id}`)} data-testid={`client-card-${c.id}`}>
              <div className="flex items-start gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#0D2E24] text-white"><Icons.Building2 className="h-5 w-5" /></div>
                <div className="min-w-0 flex-1">
                  <p className="truncate font-semibold text-slate-800">{c.name}</p>
                  <p className="truncate text-sm text-slate-500">{c.contact_name || "Sans contact"}</p>
                  <p className="truncate text-xs text-slate-400">{c.phone || c.email || ""}</p>
                </div>
                <Icons.ChevronRight className="h-4 w-4 text-slate-300" />
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
